import './register.scss';

import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Link, Switch } from '@mui/material';
import MaleIcon from '@mui/icons-material/Male';
import FemaleIcon from '@mui/icons-material/Female';
import ManIcon from '@mui/icons-material/Man';
import WomanIcon from '@mui/icons-material/Woman';
import { Cake, Check, ChevronDown, ChevronLeft, ChevronRight, Eye, EyeOff, Heart, Lock, Mail, MapPin, User } from 'lucide-react';
import { getDaysInMonth, getMonth } from 'date-fns';
import { getUser } from 'selectors/AuthenticationSelectors';
import { useDispatch, useSelector } from 'react-redux';
import RangeSliderComponent from 'pages/Mainpage/components/RangeSliderComponent';
import { stepDescription, stepTitles } from './data';
import useHistoryPush from 'core/useHistoryPush';
import { AGB_PATH, CONTENT_GUIDELINES_PATH, MAIN_PATH } from 'models/Paths';
import isValidEmail from 'core/typeguards/isValidEmail';
import { IRegistrationCredentials } from 'models/authentication/registration/IRegistrationCredentials';
import { GenderSearchTraits, GenderTraits } from 'temp/models/BerndUserTraits';
import { USER_DEFAULT_DATE } from 'models/user/IUser';
import { Genders } from 'models/user/Gender';
import useUniqueId from 'core/useUniqueId';
import AuthenticationActionCreator from 'actions/AuthenticationActionCreator';
import { isError } from 'models/core/error/IError';
import NotificationActionCreator from 'services/Notifications/actions/NotificationActionCreator';
import { getRegistrationMessage } from 'config/errorMessages';
import LocationAutocomplete from 'components/LocationAutocomplete';
import CountrySelect from 'components/CountrySelect/CountrySelect';
import Config from 'config/config';

const TOTAL_STEPS = 6;

const MONTH_NAMES = [
    'Januar',
    'Februar',
    'März',
    'April',
    'Mai',
    'Juni',
    'Juli',
    'August',
    'September',
    'Oktober',
    'November',
    'Dezember',
];

const Register: React.FC<any> = () => {
    const [step, setStep] = useState(1);
    const user = useSelector(getUser);

    const [distance, setDistance] = useState<number>(user?.Distance || 100);
    const [autoDetectLocation, setAutoDetectLocation] = useState<boolean>(false);

    const history = useHistory();

    const [selectedDate, handleDateChange] = useState<Date>(USER_DEFAULT_DATE);
    const [name, setName] = useState<string>('');
    const [email, setEmail] = useState<string>();
    const [password, setPassword] = useState<string>();
    const [repeatPW, setRepeatPW] = useState<string>();
    const [gender, setSelectedGender] = useState(GenderTraits.MALE);
    const [selectedZip, setSelectedZip] = useState<string | undefined>(undefined);
    const [selectedCity, setSelectedCity] = useState<string | undefined>(undefined);
    const [selectedCountry, setSelectedCountry] = useState<string>('de');
    const [loading, setLoading] = useState<boolean>(false);
    const [searchGender, setSearchGender] = useState(GenderSearchTraits.FEMALE);
    const [optIn, setOptIn] = useState<boolean>(true);
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [showRepeatPW, setShowRepeatPW] = useState<boolean>(false);

    const handleGenderSelect = (value: any) => setSelectedGender(value);
    const handleLookingFor = (value: number) => setSearchGender(value);

    const setDay = (day: number) => {
        const newDate = new Date(selectedDate);
        newDate.setDate(day);
        handleDateChange(newDate);
    };
    const setMonth = (month: number) => {
        const newDate = new Date(selectedDate);
        newDate.setMonth(month - 1);
        handleDateChange(newDate);
    };
    const setYear = (year: number) => {
        const newDate = new Date(selectedDate);
        newDate.setFullYear(year);
        handleDateChange(newDate);
    };

    const currentYear = new Date().getFullYear();
    const dayOptions = Array.from({ length: getDaysInMonth(selectedDate) }, (_, i) => i + 1);
    const yearOptions = Array.from({ length: Config.MAX_AGE - Config.MIN_AGE }, (_, i) => currentYear - Config.MAX_AGE + i + 1).reverse();

    const profileId = history.location.pathname.includes('profile')
        ? history.location.pathname.replace('profile', 'stranger-profile')
        : '/matchgame';

    const progressSegments = Array.from({ length: TOTAL_STEPS }, (_, i) => i < step);

    const handleTermsChange = () => setOptIn((prev) => !prev);

    const handleNext = () => {
        if (isStepValid() && step < TOTAL_STEPS) {
            setStep((prevStep) => prevStep + 1);
        }
    };

    const handleBack = () => {
        if (step > 1) {
            setStep(step - 1);
        }
    };

    const handleSubmit = () => {
        handleRegister();
    };

    const id = useUniqueId();
    const dispatch = useDispatch();
    const handleClose = useHistoryPush(MAIN_PATH);

    const isStepValid = () => {
        switch (step) {
            case 1:
                return name.trim() !== '';
            case 2:
                return selectedDate !== null;
            case 5:
                return selectedZip?.trim() !== undefined && selectedCity?.trim() !== undefined;
            case 6:
                return Boolean(optIn && email?.trim() && password?.trim() && password === repeatPW);
            default:
                return true;
        }
    };

    const handleRegister = async () => {
        if (
            name &&
            email &&
            isValidEmail(email) &&
            gender &&
            selectedCity &&
            selectedZip &&
            selectedDate &&
            password &&
            repeatPW &&
            repeatPW === password
        ) {
            const register: IRegistrationCredentials = {
                name: name,
                email: email,
                password: password,
                dateOfBirth: selectedDate.toISOString(),
                gender: gender === GenderTraits.MALE ? Genders.MALE : Genders.FEMALE,
                country: selectedCountry,
                city: selectedCity,
                postalCode: selectedZip,
                deviceId: id,
                pushPreference: true,
                profileImageEncoded: '',
                firebaseId: '',
                platform: 'ios',
                preferredGender: searchGender,
            };

            setLoading(true);
            const result = await dispatch(AuthenticationActionCreator.registerUser(register));
            setLoading(false);

            try {
                if (isError(result.payload.result)) {
                    dispatch(
                        NotificationActionCreator.enqueueSnackbar({
                            key: String(new Date().getTime() + Math.random()),
                            message:
                                'Registrierung Fehlgeschlagen!' +
                                getRegistrationMessage(
                                    result.payload.result._embedded.errors.length > 0
                                        ? result.payload.result._embedded.errors[0].message
                                        : result.payload.result.message
                                ),
                            options: { variant: 'error' },
                        })
                    );
                } else {
                    handleClose();
                    history.push(profileId);
                }
            } catch (e) {
                /* noop */
            }
        } else {
            const errorArray: string[] = [];
            if (!name || name.trim() === '') {
                errorArray.push('Bitte gebe deinen Namen an.');
            }
            if (!selectedZip) {
                errorArray.push('Bitte gebe deinen Wohnort an.');
            }
            if (!email || email.trim() === '') {
                errorArray.push('Bitte gebe deine E-Mail Adresse an.');
            } else if (!isValidEmail(email.trim())) {
                errorArray.push('Bitte gebe eine gültige E-Mail Adresse an.');
            }
            if (!optIn) {
                errorArray.push('Bitte bestätige die AGB');
            }
            if (!password || password.trim() === '') {
                errorArray.push('Bitte gebe ein Passwort ein.');
            } else if (!repeatPW || repeatPW.trim() === '') {
                errorArray.push('Bitte wiederhole dein Passwort.');
            } else if (password.trim() !== repeatPW.trim()) {
                errorArray.push('Dein wiederholtes Passwort stimmt nicht mit deinem Passwort überein.');
            }
            if (errorArray.length < 1) {
                errorArray.push('Ein unbekannter Fehler ist aufgetreten.');
            }
            for (const error of errorArray) {
                dispatch(
                    NotificationActionCreator.enqueueSnackbar({
                        key: String(new Date().getTime() + Math.random()),
                        message: error,
                        options: { variant: 'error' },
                    })
                );
            }
        }
    };

    const brandName = Config.GLOBAL_SITE_NAME.toLowerCase();
    const brandSplit = Math.ceil(brandName.length / 2);

    const stepIcons = [
        <Heart key="1" fill={Config.GLOBAL_PRIMARY_COLOR} stroke={Config.GLOBAL_PRIMARY_COLOR} strokeWidth={1.5} />,
        <Cake key="2" strokeWidth={2} />,
        <MaleIcon key="3" />,
        <User key="4" fill={Config.GLOBAL_PRIMARY_COLOR} strokeWidth={1.5} />,
        <MapPin key="5" strokeWidth={2} />,
        <Heart key="6" fill={Config.GLOBAL_PRIMARY_COLOR} stroke={Config.GLOBAL_PRIMARY_COLOR} strokeWidth={1.5} />,
    ];

    const genderChoices = [
        { value: GenderTraits.MALE, label: 'Männlich', icon: <MaleIcon /> },
        { value: GenderTraits.FEMALE, label: 'Weiblich', icon: <FemaleIcon /> },
    ];

    const lookingForChoices = [
        { value: GenderSearchTraits.MALE, label: 'Männer', icon: <ManIcon /> },
        { value: GenderSearchTraits.FEMALE, label: 'Frauen', icon: <WomanIcon /> },
    ];

    return (
        <div className="onb">
            <div className="onb__decor" aria-hidden>
                {Array.from({ length: 6 }).map((_, i) => (
                    <span key={i} className="onb__decor-heart">
                        <Heart fill="currentColor" stroke="none" />
                    </span>
                ))}
            </div>

            <div className="onb__card">
                <div className="onb__brand">
                    <span className="onb__brand-heart">
                        <Heart fill={Config.GLOBAL_PRIMARY_COLOR} stroke={Config.GLOBAL_PRIMARY_COLOR} strokeWidth={1.5} />
                    </span>
                    <span>
                        {brandName.slice(0, brandSplit)}
                        <span className="onb__brand-accent">{brandName.slice(brandSplit)}</span>
                    </span>
                </div>

                <div className="onb__progress">
                    <div className="onb__progress-track">
                        {progressSegments.map((on, i) => (
                            <span key={i} className={`onb__progress-seg${on ? ' onb__progress-seg--on' : ''}`} />
                        ))}
                    </div>
                    <span className="onb__progress-count">
                        {step} / {TOTAL_STEPS}
                    </span>
                </div>

                <div className="onb__icon">{stepIcons[step - 1]}</div>

                <h1 className="onb__title">{stepTitles[step - 1]}</h1>
                <p className="onb__subtitle">{stepDescription[step - 1]}</p>

                <div className="onb__content">
                    {step === 1 && (
                        <label className="onb__input">
                            <User />
                            <input
                                className="onb__input-field"
                                type="text"
                                placeholder="Dein Name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                autoFocus
                            />
                        </label>
                    )}

                    {step === 2 && (
                        <div className="onb__date">
                            <div className="onb__date-field">
                                <span className="onb__date-label">Tag</span>
                                <div className="onb__select">
                                    <select value={selectedDate.getDate()} onChange={(e) => setDay(Number(e.target.value))}>
                                        {dayOptions.map((d) => (
                                            <option key={d} value={d}>
                                                {d}
                                            </option>
                                        ))}
                                    </select>
                                    <ChevronDown strokeWidth={2} />
                                </div>
                            </div>

                            <div className="onb__date-field">
                                <span className="onb__date-label">Monat</span>
                                <div className="onb__select">
                                    <select value={getMonth(selectedDate) + 1} onChange={(e) => setMonth(Number(e.target.value))}>
                                        {MONTH_NAMES.map((monthName, i) => (
                                            <option key={monthName} value={i + 1}>
                                                {monthName}
                                            </option>
                                        ))}
                                    </select>
                                    <ChevronDown strokeWidth={2} />
                                </div>
                            </div>

                            <div className="onb__date-field">
                                <span className="onb__date-label">Jahr</span>
                                <div className="onb__select">
                                    <select value={selectedDate.getFullYear()} onChange={(e) => setYear(Number(e.target.value))}>
                                        {yearOptions.map((y) => (
                                            <option key={y} value={y}>
                                                {y}
                                            </option>
                                        ))}
                                    </select>
                                    <ChevronDown strokeWidth={2} />
                                </div>
                            </div>
                        </div>
                    )}

                    {step === 3 && (
                        <div className="onb__choices">
                            {genderChoices.map((choice) => (
                                <button
                                    key={choice.value}
                                    type="button"
                                    className={`onb__choice${gender === choice.value ? ' onb__choice--active' : ''}`}
                                    onClick={() => handleGenderSelect(choice.value)}
                                >
                                    {gender === choice.value && (
                                        <span className="onb__choice-check">
                                            <Check strokeWidth={3} />
                                        </span>
                                    )}
                                    {choice.icon}
                                    <span className="onb__choice-label">{choice.label}</span>
                                </button>
                            ))}
                        </div>
                    )}

                    {step === 4 && (
                        <div className="onb__choices">
                            {lookingForChoices.map((choice) => (
                                <button
                                    key={choice.value}
                                    type="button"
                                    className={`onb__choice${searchGender === choice.value ? ' onb__choice--active' : ''}`}
                                    onClick={() => handleLookingFor(choice.value)}
                                >
                                    {searchGender === choice.value && (
                                        <span className="onb__choice-check">
                                            <Check strokeWidth={3} />
                                        </span>
                                    )}
                                    {choice.icon}
                                    <span className="onb__choice-label">{choice.label}</span>
                                </button>
                            ))}
                        </div>
                    )}

                    {step === 5 && (
                        <div className="onb__mui">
                            <div className="onb__field-group">
                                <span className="onb__field-label">Land</span>
                                <CountrySelect
                                    selectedCountry={selectedCountry}
                                    onCountryChange={(newCountry: string) => {
                                        setSelectedCity(undefined);
                                        setSelectedZip(undefined);
                                        setSelectedCountry(newCountry);
                                    }}
                                />
                            </div>

                            <div className="onb__field-group">
                                <span className="onb__field-label">Wohnort</span>
                                <LocationAutocomplete
                                    countryCode={selectedCountry}
                                    inputStyle={{ borderRadius: '14px' }}
                                    value={selectedCity}
                                    onLocationMatched={(city, zip) => {
                                        setSelectedZip(zip);
                                        setSelectedCity(city);
                                    }}
                                    placeholder="Stadt oder PLZ"
                                />
                            </div>

                            <div className="onb__field-group">
                                <RangeSliderComponent value={distance} onChange={setDistance} />
                            </div>

                            <div className="onb__toggle-row">
                                <MapPin strokeWidth={2} />
                                <span className="onb__toggle-text">Meinen Standort automatisch erkennen</span>
                                <Switch
                                    checked={autoDetectLocation}
                                    onChange={(e) => setAutoDetectLocation(e.target.checked)}
                                    sx={{
                                        '& .Mui-checked': { color: Config.GLOBAL_PRIMARY_COLOR },
                                        '& .Mui-checked + .MuiSwitch-track': { backgroundColor: Config.GLOBAL_PRIMARY_COLOR },
                                    }}
                                />
                            </div>
                        </div>
                    )}

                    {step === 6 && (
                        <>
                            <label className="onb__input">
                                <Mail />
                                <input
                                    className="onb__input-field"
                                    type="email"
                                    placeholder="E-Mail"
                                    value={email ?? ''}
                                    onChange={(e) => setEmail(e.target.value)}
                                />
                            </label>

                            <label className="onb__input">
                                <Lock />
                                <input
                                    className="onb__input-field"
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder="Passwort"
                                    value={password ?? ''}
                                    onChange={(e) => setPassword(e.target.value)}
                                />
                                <button
                                    type="button"
                                    className="onb__input-toggle"
                                    onClick={() => setShowPassword((prev) => !prev)}
                                    aria-label="Passwort anzeigen"
                                >
                                    {showPassword ? <EyeOff /> : <Eye />}
                                </button>
                            </label>

                            <label className="onb__input">
                                <Lock />
                                <input
                                    className="onb__input-field"
                                    type={showRepeatPW ? 'text' : 'password'}
                                    placeholder="Passwort wiederholen"
                                    value={repeatPW ?? ''}
                                    onChange={(e) => setRepeatPW(e.target.value)}
                                />
                                <button
                                    type="button"
                                    className="onb__input-toggle"
                                    onClick={() => setShowRepeatPW((prev) => !prev)}
                                    aria-label="Passwort wiederholen anzeigen"
                                >
                                    {showRepeatPW ? <EyeOff /> : <Eye />}
                                </button>
                            </label>

                            <div className="onb__terms">
                                <span
                                    className={`onb__terms-box${optIn ? ' onb__terms-box--checked' : ''}`}
                                    role="checkbox"
                                    aria-checked={optIn}
                                    tabIndex={0}
                                    onClick={handleTermsChange}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            handleTermsChange();
                                        }
                                    }}
                                >
                                    {optIn && <Check strokeWidth={3} />}
                                </span>
                                <span className="onb__terms-text">
                                    Ich bestätige, dass ich mindestens 18 Jahre alt bin. Ich habe die{' '}
                                    <Link target="_blank" href={AGB_PATH}>
                                        Nutzungsbedingungen
                                    </Link>
                                    ,{' '}
                                    <Link target="_blank" href={CONTENT_GUIDELINES_PATH}>
                                        Community Guidelines und Richtlinien zu verbotenen Inhalten
                                    </Link>{' '}
                                    gelesen und akzeptiere diese.
                                </span>
                            </div>
                        </>
                    )}
                </div>

                <div className="onb__actions">
                    {step > 1 && (
                        <button type="button" className="onb__btn onb__btn--back" onClick={handleBack}>
                            <ChevronLeft strokeWidth={2.5} />
                            Zurück
                        </button>
                    )}

                    {step === TOTAL_STEPS ? (
                        <button
                            type="button"
                            className="onb__btn onb__btn--next"
                            onClick={handleSubmit}
                            disabled={!isStepValid() || loading}
                        >
                            <Heart fill="#fff" stroke="#fff" strokeWidth={1.5} />
                            Jetzt kostenlos registrieren
                        </button>
                    ) : (
                        <button
                            type="button"
                            className="onb__btn onb__btn--next"
                            onClick={handleNext}
                            disabled={!isStepValid()}
                        >
                            Weiter
                            <ChevronRight strokeWidth={2.5} />
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Register;
